/**
 * Compatibility shim for fast-xml-parser v5.
 * @aws-sdk/client-s3 expects a named "parse" export; v5 only exports XMLParser class.
 * This module is aliased as "fast-xml-parser" in Vite so the SDK gets a parse() function.
 *
 * Normalizes v4-style options used by the SDK to the parser's constructor options:
 * - parseNodeValue (SDK/v4) -> parseTagValue (parser)
 * - tagValueProcessor: SDK uses (val, tagName), parser calls (tagName, val, jPath, hasAttributes, isLeafNode)
 */
import { XMLParser } from 'fast-xml-parser-pkg';

/**
 * Normalize options from @aws-sdk v4-style parse() API to XMLParser constructor options.
 * @param {object} [options] Options passed by the SDK to parse(xml, options).
 * @returns {object} Options suitable for new XMLParser(normalized).
 */
function normalizeOptions(options) {
  if (!options || typeof options !== 'object') return {};
  const normalized = { ...options };
  if ('parseNodeValue' in normalized) {
    normalized.parseTagValue = normalized.parseNodeValue;
    delete normalized.parseNodeValue;
  }
  if (typeof normalized.tagValueProcessor === 'function') {
    const userProcessor = normalized.tagValueProcessor;
    // Parser calls (tagName, val, jPath, hasAttributes, isLeafNode); SDK expects (val, tagName).
    normalized.tagValueProcessor = (tagName, val, jPath, hasAttributes, isLeafNode) =>
      userProcessor(val, tagName);
  }
  return normalized;
}

/** @param {string | Uint8Array} xmlData @param {any} [options] Parser options (v4-style). @returns {any} */
export function parse(xmlData, options) {
  const parser = new XMLParser(normalizeOptions(options));
  return parser.parse(xmlData);
}
